<script lang="ts">
    import { onMount, onDestroy } from 'svelte';
    import { api } from '../js/api.js';
  import { link } from 'svelte-spa-router';
  import { location } from '../js/spaLocation';
  import UserAvatar from './UserAvatar.svelte';
  import { auth, userProfileImageUrl } from "../js/auth.js";
  import { writable } from 'svelte/store';
  import { push } from 'svelte-spa-router';
  import { toast } from "../js/toast.js";
  import { get } from 'svelte/store';
  import type { ApiResponse } from '../js/apiTypes.js';
  import {
    GUEST_TRIAL_STORAGE_KEY,
    GUEST_ILLUMINATOR_DRAFT_KEY,
    GUEST_GAPFINDER_DRAFT_KEY,
    guestTrialProduct,
    type GuestTrialAccent,
    type GuestTrialSlug,
  } from '../../js/guestTrialProducts';
  import { loadGuestGapfinderFile, clearGuestGapfinderFile } from '../../js/guestGapfinderFileStore';
  import GuestTrialResumeModal from '../../components/brief/GuestTrialResumeModal.svelte';
  import { TeamBillingStrip } from './app';
  import { GUEST_BUILDER_ANSWERS_KEY, GUEST_BUILDER_NAME_KEY } from '../../components/brief-builder/questions';

  type GuestTrialKind = 'btob' | 'illuminator' | 'gapfinder';

  interface GuestTrialModalMeta {
    name: string;
    tagline: string;
    icon: string;
    accent: GuestTrialAccent;
  }

  let showGuestTrialModal = false;
  let guestTrialKind: GuestTrialKind | null = null;
  let guestTrialMeta: GuestTrialModalMeta | null = null;
  
    interface MenuItem {
        component?: any;
        name: string;
        icon: string;
        useModal?: boolean;
        onClick?: () => void;
        href?: string;
        className?: string;
        id?: string;
        hidden?: boolean;
        group?: string;
    }
    export let components: Record<string, MenuItem>;
    export let dashboardType = '';

    interface VersionInfo {
        build_number?: string;
        commit_hash?: string;
    }

    let versionInfo: VersionInfo | null = null;
    let displayVersion = __APP_VERSION__ || 'dev';
    
    let currentComponent: any = null;
    let currentComponentName: string | null = null;
    let currentComponentKey: string | null = null;
    let isMenuCollapsed = false;

    // Map URL paths to component names for user dashboard
    const pathToComponentMap: Record<string, string> = {
        '/dashboard': 'UserList',
        '/brief-builder': 'BriefBuilder',
        '/briefs': 'Brief',
        '/brief-new': 'Brief',
        '/brief': 'Brief',
        '/assessments': 'Assessments',
        '/assessments/new': 'Assessments',
        '/gapfinder': 'Gapfinder',
        '/research-library': 'TeamKnowledge',
        '/team-knowledge': 'TeamKnowledge',
        '/ai-processes': 'AiJobs',
        '/learning': 'Blog',
        '/team': 'Team',
        '/payment-plan': 'PaymentPlan',
        '/payment-plans': 'PaymentPlanSelect',
        '/invoices': 'Invoices',
        '/profile': 'Profile'
    };

    // Special sub-route mappings (for detail pages)
    const subRouteMap: Record<string, string> = {
        '/invoices/': 'InvoiceDetail'
    };
    
    // Check if path starts with any mapped route
    function getComponentForPath(path: string): string | null {
        // Direct match first
        if (pathToComponentMap[path]) {
            return pathToComponentMap[path];
        }
        // Check for special sub-routes (detail pages)
        for (const [prefix, componentName] of Object.entries(subRouteMap)) {
            if (path.startsWith(prefix) && path.length > prefix.length) {
                return componentName;
            }
        }
        // Check for general sub-routes (e.g., /briefs/123)
        for (const [route, componentName] of Object.entries(pathToComponentMap)) {
            if (path.startsWith(route + '/')) {
                return componentName;
            }
        }
        return null;
    }

    function routeTo(compKey: string) {
        currentComponentKey = compKey;
        currentComponentName = components[compKey]?.name || null;
        currentComponent = components[compKey]?.component || null;
        
        const routePath = Object.entries(pathToComponentMap).find(([_, comp]) => comp === compKey)?.[0];
        if (routePath && routePath !== $location) {
            push(routePath);
        }
    }

    // Extract invoice UUID from path when on /invoices/:uuid (for InvoiceDetail)
    // Use both $location and window.location.hash so we always get the UUID regardless of router timing
    $: invoiceUuidFromPath = (() => {
        const fromStore = ($location || '').replace(/^\#?\/?/, '/');
        const fromHash = (typeof window !== 'undefined' && window.location.hash)
            ? window.location.hash.replace(/^#\/?/, '/')
            : '';
        const path = fromStore || fromHash;
        const normalized = path.startsWith('/') ? path : '/' + path;
        if (!normalized.startsWith('/invoices/') || normalized === '/invoices') return undefined;
        const rest = normalized.slice('/invoices/'.length).split('?')[0];
        const segment = rest.split('/')[0];
        return (segment && segment.length > 0) ? segment : undefined;
    })();

    // Sync component with current URL - reactive to location changes
    $: if ($location) {
        const path = $location;
        const componentKey = getComponentForPath(path);
        if (componentKey && components[componentKey]) {
            if (currentComponentKey !== componentKey) {
                currentComponentKey = componentKey;
                currentComponentName = components[componentKey].name || null;
                currentComponent = components[componentKey].component || null;
            }
        } else if (path === '/dashboard' || path === '/dashboard/') {
            if (components['UserList'] && currentComponentKey !== 'UserList') {
                currentComponentKey = 'UserList';
                currentComponentName = components['UserList'].name || null;
                currentComponent = components['UserList'].component || null;
                if (path !== '/dashboard') {
                    push('/dashboard');
                }
            }
        }
    }

    export let authData: any = writable([]);
    auth.subscribe((data: any) => {
        authData = data;
    });

    const TEAM_CACHE_KEY = 'xplorit_sidebar_team';

    function readTeamCache(): { name: string; teams: any[] } {
        try {
            const raw = localStorage.getItem(TEAM_CACHE_KEY);
            if (raw) return JSON.parse(raw);
        } catch { /* ignore corrupt cache */ }
        return { name: '', teams: [] };
    }

    function writeTeamCache(name: string, teams: any[]) {
        try {
            localStorage.setItem(TEAM_CACHE_KEY, JSON.stringify({ name, teams }));
        } catch { /* quota errors etc */ }
    }

    const _cached = readTeamCache();
    let userTeams: any[] = Array.isArray(_cached?.teams) ? _cached.teams : [];
    let activeTeamName = _cached?.name || '';
    let showTeamSwitcher = false;
    let teamsLoaded = userTeams.length > 0;

    let cachedUserDisplayName = '';

    $: {
        const fn = authData?.user?.first_name || '';
        const ln = authData?.user?.last_name || '';
        const combined = `${fn} ${ln}`.trim();
        if (combined) cachedUserDisplayName = combined;
    }

    $: userDisplayName = cachedUserDisplayName;

    function handleWindowClick(e: MouseEvent) {
        if (showTeamSwitcher) {
            const target = e.target as HTMLElement;
            if (!target.closest('.team-inline')) {
                showTeamSwitcher = false;
            }
        }
    }

    function toggleTeamSwitcher(e: MouseEvent) {
        e.preventDefault();
        e.stopPropagation();
        if (userTeams.length > 1) {
            showTeamSwitcher = !showTeamSwitcher;
        }
    }

    async function loadUserTeams() {
        if (!authData?.user?.uuid) return;
        try {
            const res: any = await api.getUserTeams(authData.user.uuid);
            if (res?.success) {
                const fetched = Array.isArray(res.data?.teams) ? res.data.teams : [];
                userTeams = fetched;
                teamsLoaded = true;
                const activeTeam = fetched.find((t: any) => t?.team?.uuid === authData.user.active_team_uuid);
                let name = activeTeam?.team?.name?.trim() || '';
                if (!name && fetched.length === 1) {
                    name = fetched[0]?.team?.name?.trim() || '';
                }
                if (name) activeTeamName = name;
                writeTeamCache(activeTeamName, fetched);
            }
        } catch (err) {
            console.error('Failed to load user teams', err);
        }
    }

    $: teamDisplayLabel = activeTeamName;

    async function handleSwitchTeam(teamUuid: string) {
        if (teamUuid === authData.user.active_team_uuid) {
            showTeamSwitcher = false;
            return;
        }
        const res = await api.switchTeam(authData.user.uuid, teamUuid);
        if (res.success) {
            auth.update((state: any) => {
                state.user.active_team_uuid = teamUuid;
                return state;
            });
            showTeamSwitcher = false;
            await loadUserTeams();
            // Reload current page to refresh team-scoped data
            window.location.reload();
        }
    }

    onMount(() => {
        void (async () => {
            try {
                const response = await fetch('/version.json');
                if (response.ok) {
                    versionInfo = await response.json();
                    if (versionInfo?.build_number) {
                        displayVersion = versionInfo.build_number;
                    }
                }
            } catch {
                // Version info not available in local dev
            }
        })();

        loadUserTeams();
        window.addEventListener('team-updated', loadUserTeams);
        // The reactive $: if ($location) block already handles setting the correct component.
        // Only fallback to default if no component was set by the reactive block.
        if (!currentComponent) {
            const path = $location || '/dashboard';
            const compKey = getComponentForPath(path);
            if (compKey) {
                currentComponentKey = compKey;
                currentComponentName = components[compKey]?.name || null;
                currentComponent = components[compKey]?.component || null;
            } else {
                const firstComponent = Object.keys(components).find(key => components[key].component);
                if (firstComponent) {
                    routeTo(firstComponent);
                }
            }
        }
        
        setTimeout(() => {
            restoreGuestTrialDraft();
        }, 1000);
    });

    onDestroy(() => {
        window.removeEventListener('team-updated', loadUserTeams);
    });

    function clearGuestBtobDraft() {
        localStorage.setItem('localBriefAnswers_guest', '');
        localStorage.setItem('localBriefAnswers_guest_array', '');
        localStorage.setItem('localBriefName_guest', '');
        localStorage.removeItem(GUEST_BUILDER_ANSWERS_KEY);
        localStorage.removeItem(GUEST_BUILDER_NAME_KEY);
        localStorage.removeItem('guestBriefBuilderStep');
    }

    function clearGuestIlluminatorDraft() {
        localStorage.removeItem(GUEST_ILLUMINATOR_DRAFT_KEY);
    }

    function clearGuestGapfinderDraft() {
        localStorage.removeItem(GUEST_GAPFINDER_DRAFT_KEY);
        void clearGuestGapfinderFile();
    }

    function clearGuestTrialMeta() {
        localStorage.removeItem(GUEST_TRIAL_STORAGE_KEY);
    }

    function restoreGuestTrialDraft() {
        const trialProduct = localStorage.getItem(GUEST_TRIAL_STORAGE_KEY);
        const briefAnswers = localStorage.getItem('localBriefAnswers_guest');
        const builderAnswers = localStorage.getItem(GUEST_BUILDER_ANSWERS_KEY);
        const illuminatorDraft = localStorage.getItem(GUEST_ILLUMINATOR_DRAFT_KEY);
        const gapfinderDraft = localStorage.getItem(GUEST_GAPFINDER_DRAFT_KEY);

        let gapfinderParsed: { content?: string; fileName?: string } | null = null;
        try {
            gapfinderParsed = gapfinderDraft ? JSON.parse(gapfinderDraft) : null;
        } catch {
            gapfinderParsed = null;
        }

        let hasBuilder = false;
        try {
            const parsed = builderAnswers ? JSON.parse(builderAnswers) : null;
            hasBuilder = Boolean(parsed && (parsed.decision || parsed.territory || parsed.people));
        } catch {
            hasBuilder = false;
        }

        const hasBtob = (briefAnswers && briefAnswers !== '{}') || hasBuilder;
        const hasIlluminator = illuminatorDraft && illuminatorDraft !== '{}';
        const hasGapfinder = Boolean(
            gapfinderParsed && (gapfinderParsed.content || gapfinderParsed.fileName),
        );

        if (!hasBtob && !hasIlluminator && !hasGapfinder) return;

        let slug: GuestTrialSlug = 'btob';
        let kind: GuestTrialKind = 'btob';
        if (trialProduct === 'btob' || hasBtob) {
            slug = 'btob';
            kind = 'btob';
        } else if (trialProduct === 'illuminator' || hasIlluminator) {
            slug = 'illuminator';
            kind = 'illuminator';
        } else if (trialProduct === 'gapfinder' || hasGapfinder) {
            slug = 'gapfinder';
            kind = 'gapfinder';
        }

        const product = guestTrialProduct(slug);
        guestTrialKind = kind;
        guestTrialMeta = product
            ? {
                  name: product.name,
                  tagline: product.tagline,
                  icon: product.icon,
                  accent: product.accent,
              }
            : {
                  name: 'trial',
                  tagline: '',
                  icon: 'file alternate',
                  accent: 'teal',
              };
        showGuestTrialModal = true;
    }

    function handleGuestTrialContinue() {
        const kind = guestTrialKind;
        guestTrialKind = null;
        guestTrialMeta = null;

        if (kind === 'btob') createBrief();
        else if (kind === 'illuminator') restoreGuestIlluminator();
        else if (kind === 'gapfinder') restoreGuestGapfinder();
    }

    function handleGuestTrialDiscard() {
        clearGuestBtobDraft();
        clearGuestIlluminatorDraft();
        clearGuestGapfinderDraft();
        clearGuestTrialMeta();
        guestTrialKind = null;
        guestTrialMeta = null;
    }

    async function restoreGuestIlluminator() {
        try {
            const raw = localStorage.getItem(GUEST_ILLUMINATOR_DRAFT_KEY);
            if (!raw) return;
            const answers = JSON.parse(raw);
            const payload = {
                name: answers.name,
                title: answers.name,
                the_decision: answers.the_decision,
                the_assumption: answers.the_assumption,
                the_stakes: answers.the_stakes,
                the_customer: answers.the_customer,
                what_you_know: answers.what_you_know,
                time_budget: answers.time_budget,
            };
            const body: any = await api.createAssessment(payload);
            const uuid = body?.data?.assessment?.uuid;
            if (!body?.success || !uuid) {
                toast.showNotification(false, body?.message || 'Could not restore Illuminator trial');
                return;
            }
            try {
                await api.enqueueAssessmentGenerationAsync(uuid);
            } catch (e) {
                console.error('Failed to enqueue Illuminator generation', e);
            }
            clearGuestIlluminatorDraft();
            clearGuestTrialMeta();
            window.location.href = `/#/assessments/${uuid}`;
        } catch (error) {
            console.error('Failed to restore Illuminator trial', error);
        }
    }

    async function restoreGuestGapfinder() {
        try {
            const raw = localStorage.getItem(GUEST_GAPFINDER_DRAFT_KEY);
            if (!raw) return;
            const draft = JSON.parse(raw);
            let createRes: any;

            if (draft.mode === 'upload') {
                const file = await loadGuestGapfinderFile();
                if (!file) {
                    toast.showNotification(false, 'Could not restore uploaded brief file.');
                    return;
                }
                const fd = new FormData();
                fd.append('file', file);
                if (draft.title) fd.append('title', draft.title);
                createRes = await api.createGapfinderFromUpload(fd);
            } else {
                createRes = await api.createGapfinderFromText({
                    content: draft.content,
                    title: draft.title || null,
                });
            }

            const analysisUuid = createRes?.data?.analysis?.uuid;
            if (!createRes?.success || !analysisUuid) {
                toast.showNotification(false, createRes?.message || 'Could not restore GapFinder trial');
                return;
            }
            try {
                await api.enqueueGapfinderGenerationAsync(analysisUuid);
            } catch (e) {
                console.error('Failed to enqueue GapFinder generation', e);
            }
            clearGuestGapfinderDraft();
            clearGuestTrialMeta();
            window.location.href = `/#/gapfinder/${analysisUuid}`;
        } catch (error) {
            console.error('Failed to restore GapFinder trial', error);
        }
    }

    async function createBrief() {
            try {
                const builderRaw = localStorage.getItem(GUEST_BUILDER_ANSWERS_KEY);
                if (builderRaw) {
                    let answers: Record<string, unknown> = {};
                    try {
                        answers = JSON.parse(builderRaw);
                    } catch {
                        answers = {};
                    }
                    const name =
                        (typeof answers.decision === 'string' && answers.decision.slice(0, 80)) ||
                        localStorage.getItem(GUEST_BUILDER_NAME_KEY) ||
                        'Guest research brief';
                    const body = await api.createBriefBuilder(name) as any;
                    if (body?.success && body?.data?.brief?.uuid) {
                        const uuid = body.data.brief.uuid;
                        const p = (answers.practicalities || {}) as Record<string, unknown>;
                        const apiAnswers = {
                            decision: answers.decision || '',
                            territory: answers.territory || '',
                            territory_label: answers.territoryLabel || answers.territory_label || '',
                            honest_question: answers.honestQuestion || answers.honest_question || '',
                            people: answers.people || '',
                            what_you_know: answers.whatYouKnow || answers.what_you_know || '',
                            insider_fact: answers.insiderFact || answers.insider_fact || '',
                            territory_q1: answers.territoryQ1 || answers.territory_q1 || '',
                            territory_q2: answers.territoryQ2 || answers.territory_q2 || '',
                            practicalities: {
                                budget: p.budget || '',
                                budget_unknown: !!(p.budgetUnknown ?? p.budget_unknown),
                                decision_date: p.decisionDate || p.decision_date || '',
                                no_fixed_date: !!(p.noFixedDate ?? p.no_fixed_date),
                                geography: p.geography || '',
                                cross_borders: !!(p.crossBorders ?? p.cross_borders),
                                solidity: p.solidity || '',
                            },
                            unknowns: Array.isArray(answers.unknowns) ? answers.unknowns : [],
                        };
                        await api.saveBriefBuilderAnswers(uuid, apiAnswers);
                        clearGuestBtobDraft();
                        clearGuestTrialMeta();
                        window.location.href = `/#/brief-builder/${uuid}/continue`;
                    }
                    return;
                }

                const briefName = localStorage.getItem('localBriefName_guest');
                if (!briefName) return;
                let body = await api.addXploritAndBrief(briefName) as unknown as ApiResponse<{ xploritBrief: { uuid: string } }>;
                if(body.success && body.data) {
                      const localRecord = localStorage.getItem('localBriefAnswers_guest');
                      const localArrayRecord = localStorage.getItem('localBriefAnswers_guest_array');
                      if(localRecord && localArrayRecord){
                          await api.saveGuestBriefAnswer(body.data.xploritBrief.uuid, localArrayRecord);
                      }
                  localStorage.setItem('localBriefAnswers_guest','');
                  localStorage.setItem('localBriefAnswers_guest_array','');
                  localStorage.setItem('localBriefName_guest','');
                  localStorage.removeItem(GUEST_TRIAL_STORAGE_KEY);
                  localStorage.setItem('brief_guest', 'true');
                  window.location.href = `/#/brief/${body.data.xploritBrief.uuid}/continue`;
                }
            } catch (error) {
                console.error('Failed to create brief', error);
            }
    }
</script>



<svelte:window on:click={handleWindowClick} />

{#if guestTrialMeta}
  <GuestTrialResumeModal
    bind:show={showGuestTrialModal}
    productName={guestTrialMeta.name}
    productTagline={guestTrialMeta.tagline}
    icon={guestTrialMeta.icon}
    accent={guestTrialMeta.accent}
    on:continue={handleGuestTrialContinue}
    on:discard={handleGuestTrialDiscard}
  />
{/if}

<div class="dashboard" data-dashboard-type={dashboardType}>
    <div class="menu-left">
        <div class="menu-left-header">
            <a use:link href="/" class="logo" title="Xplorit Home"><img src="./src/assets/xplorit_color.png" alt=""></a>
            
            <div class="sidebar-account-stack">
            <div class="user-card sidebar-panel">
                <UserAvatar
                    firstName={authData.user?.first_name}
                    lastName={authData.user?.last_name}
                    email={authData.user?.email}
                    imageUrl={authData.user?.profile_image_url}
                    size={40}
                />
                <div class="user-meta">
                    <div class="name" aria-live="polite">
                        {#if userDisplayName}
                            {userDisplayName}
                        {:else}
                            <span class="name-placeholder" aria-hidden="true">&nbsp;</span>
                        {/if}
                    </div>
                    <div
                        class="team-inline"
                        class:has-multiple={userTeams.length > 1}
                        class:open={showTeamSwitcher}
                    >
                        {#if userTeams.length > 1}
                            <button
                                type="button"
                                class="team-switcher-btn"
                                aria-expanded={showTeamSwitcher}
                                aria-haspopup="listbox"
                                on:click={toggleTeamSwitcher}
                            >
                                <span class="team-switcher-value">{teamDisplayLabel || '—'}</span>
                                <i class="icon chevron {showTeamSwitcher ? 'up' : 'down'} team-chevron" aria-hidden="true"></i>
                            </button>
                        {:else if teamsLoaded}
                            <div class="team-single" title={teamDisplayLabel || undefined}>
                                <span class="team-switcher-value">{teamDisplayLabel || '—'}</span>
                            </div>
                        {:else}
                            <div class="team-single team-loading" aria-live="polite">
                                <span class="team-switcher-value">Loading team…</span>
                            </div>
                        {/if}
                        {#if showTeamSwitcher && userTeams.length > 1}
                            <div class="team-dropdown" role="listbox">
                                {#each userTeams as teamEntry}
                                    <button
                                        type="button"
                                        class="team-option"
                                        class:active={teamEntry.team.uuid === authData.user?.active_team_uuid}
                                        role="option"
                                        aria-selected={teamEntry.team.uuid === authData.user?.active_team_uuid}
                                        on:click|stopPropagation={() => handleSwitchTeam(teamEntry.team.uuid)}
                                    >
                                        <span class="team-option-name">{teamEntry.team.name}</span>
                                        <span class="team-role">{teamEntry.role}</span>
                                    </button>
                                {/each}
                            </div>
                        {/if}
                    </div>
                </div>
            </div>

            <TeamBillingStrip layout="sidebar" />
            </div>

        </div>

        <div class="menu-left-content">
            {#each Object.entries(components) as [componentName, item], idx}
                {@const prevVisible = Object.values(components).slice(0, idx).reverse().find((c) => c && !c.hidden && c.name !== 'Logout') ?? null}
                {@const visibleGroup = item.group && !item.hidden && item.name !== 'Logout' ? item.group : null}
                {@const prevGroup = prevVisible?.group ?? null}
                {@const showGroupHeading = !!visibleGroup && visibleGroup !== prevGroup}

                {#if showGroupHeading}
                    <div class="menu-group" role="group" aria-label={visibleGroup}>
                        <span class="menu-group-label">{visibleGroup}</span>
                    </div>
                {/if}

                {#if item.name !== 'Logout' && !item.hidden}
                    {@const itemHref = item.href ?? ''}
                    {#if itemHref}
                        <a use:link
                           href={itemHref}
                           class="item"
                           class:active={$location === itemHref || $location.startsWith(itemHref + '/') || ($location === '/dashboard' && itemHref === '/dashboard' && componentName === 'UserList')}
                           on:click={() => {
                               const compKey = pathToComponentMap[itemHref] || componentName;
                               if (currentComponentKey !== compKey) {
                                   currentComponentKey = compKey;
                                   currentComponentName = components[compKey]?.name || null;
                                   currentComponent = components[compKey]?.component || null;
                               }
                           }}>
                            <i class={item.icon} id={item.id}></i>
                            <span>{item.name}</span>
                        </a>
                    {:else}
                        <button type="button"
                           class="item"
                           class:active={currentComponentName === item.name}
                           on:click={() => routeTo(componentName)}>
                            <i class={item.icon} id={item.id}></i>
                            <span>{item.name}</span>
                        </button>
                    {/if}
                {/if}
            {/each}
            <!-- Logout button at the bottom, separated -->
            <div class="logout-section">
                {#each Object.entries(components) as [componentName, {name, icon, id, onClick, href, className}]}
                    {#if name === 'Logout' && onClick}
                        <div class="menu-divider"></div>
                        <button type="button"
                           class="item logout-item"
                           on:click={onClick}>
                            <i class={icon} id={id}></i>
                            <span>{name}</span>
                        </button>
                    {/if}
                {/each}
            </div>
            <!-- Version display at bottom of menu -->
            <div class="version-display">
                <span>v{displayVersion}{#if versionInfo?.commit_hash} ({versionInfo.commit_hash}){/if}</span>
            </div>
        </div>
    </div>
    <div class="ui content">
        {#if currentComponent}
            {#key `${currentComponentKey}-${invoiceUuidFromPath || ''}`}
                {#if currentComponentKey === 'InvoiceDetail'}
                    <svelte:component this={currentComponent} invoiceUuid={invoiceUuidFromPath} />
                {:else}
                    <svelte:component this={currentComponent} />
                {/if}
            {/key}
        {/if}
    </div>

    <div class="menu-bottom">
        {#each Object.entries(components) as [componentName, {name, icon, id, onClick, href, className, hidden}]}
            {#if name !== 'Logout' && !hidden}
                {#if href}
                    <a use:link
                       href={href}
                       class="item"
                       class:active={$location === href || $location.startsWith(href + '/') || ($location === '/dashboard' && href === '/dashboard' && componentName === 'UserList')}
                       on:click={() => {
                           const compKey = pathToComponentMap[href] || componentName;
                           if (currentComponentKey !== compKey) {
                               currentComponentKey = compKey;
                               currentComponentName = components[compKey]?.name || null;
                               currentComponent = components[compKey]?.component || null;
                           }
                       }}>
                        <i class={icon} id={id}></i>
                        <span>{name}</span>
                    </a>
                {:else}
                    <button type="button"
                    class="item"
                    class:active={currentComponentName === name}
                    on:click={() => routeTo(componentName)}>
                        <i class={icon} id={id}></i>
                        <span>{name}</span>
                    </button>
                {/if}
            {/if}
        {/each}
    </div>
</div>

<style>
    :global(.dashboard .menu-left-header) {
        position: relative;
        z-index: 20;
        flex-shrink: 0;
    }

    :global(.dashboard .menu-left-content) {
        position: relative;
        z-index: 1;
        padding-left: 0 !important;
        padding-right: 0 !important;
    }

    .sidebar-account-stack {
        --sidebar-panel-height: 4.25rem;
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
        width: 100%;
        margin-bottom: var(--space-4);
    }

    .sidebar-panel {
        min-height: var(--sidebar-panel-height);
        box-sizing: border-box;
    }

    .user-card {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-2) var(--space-3);
        background: var(--surface);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        overflow: visible;
    }

    .user-meta {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 2px;
    }

    .user-meta .name {
        font-size: var(--text-sm);
        font-weight: 500;
        color: var(--grey);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.25;
    }

    .sidebar-account-stack .team-switcher-btn {
        padding: 0 18px 0 0;
    }

    .sidebar-account-stack .team-switcher-value {
        font-size: 0.75rem;
        line-height: 1.2;
    }

    .menu-group {
        padding: 16px 10px 4px;
        flex-shrink: 0;
    }

    .menu-group-label {
        font-size: 0.65rem;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--grey-secondary);
    }

    .menu-left-content {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        overflow-x: hidden;
        padding-left: 0;
        padding-right: 0;
        -webkit-overflow-scrolling: touch;
        overscroll-behavior: contain;
    }

    .menu-left-content > :global(.item:not(.logout-item)) {
        flex-shrink: 0;
    }

    .item {
        display: flex !important;
        align-items: center !important;
        gap: 12px;
        text-align: start;
        padding: 10px !important;
        padding-left: 10px !important;
        padding-right: 10px !important;
        text-decoration: none !important;
        color: var(--black) !important;
        border-radius: 5px !important;
        font-weight: 500 !important;
        cursor: pointer;
        transition: background-color 0.2s, color 0.2s;
        width: 100%;
        box-sizing: border-box;
        border: none !important;
        background: none !important;
        box-shadow: none !important;
        margin: 0 !important;
        position: relative;
        min-width: 0;
    }

    .item i.icon {
        margin: 0 !important;
        width: 20px;
        min-width: 20px;
        max-width: 20px;
        text-align: center;
        flex-shrink: 0;
        background: none !important;
        border: none !important;
        padding: 0 !important;
        display: inline-block;
        line-height: 1;
    }

    .item span {
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        min-width: 0;
    }

    .item:hover {
        color: var(--white) !important;
        background-color: var(--darkprimary) !important;
    }

    .item.active {
        background-color: var(--primary) !important;
        color: var(--white) !important;
    }

    .item.active:hover {
        background-color: var(--primary) !important;
        color: var(--white) !important;
    }

    .menu-divider {
        height: 1px;
        background-color: var(--border-color);
        margin: 8px 0;
        flex-shrink: 0;
        width: 100%;
        display: block;
    }

    .logout-section {
        margin-top: auto;
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        width: 100%;
    }

    .logout-item {
        flex-shrink: 0;
    }

    .version-display {
        margin-top: 0;
        padding: 8px 16px;
        text-align: center;
        font-size: 0.65rem;
        color: var(--grey-secondary);
        opacity: 0.6;
        border-top: 1px solid var(--border-color);
        flex-shrink: 0;
    }

    .version-display span {
        display: block;
    }

    .name-placeholder {
        display: inline-block;
        min-height: 1.2em;
    }

    .team-inline {
        position: relative;
        width: 100%;
    }

    .team-switcher-value {
        display: block;
        font-size: 0.8rem;
        font-weight: 500;
        color: var(--grey-secondary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        text-align: left;
        line-height: 1.25;
    }

    .team-switcher-btn {
        display: flex;
        align-items: center;
        width: 100%;
        margin: 0;
        padding: 4px 22px 4px 0;
        border: none;
        border-radius: 4px;
        background: transparent;
        cursor: pointer;
        text-align: left;
        transition: color 0.15s, background-color 0.15s;
        position: relative;
        min-width: 0;
    }

    .team-switcher-btn .team-switcher-value {
        min-width: 0;
        flex: 1;
    }

    .team-switcher-btn:hover .team-switcher-value {
        color: var(--primary);
    }
    .team-switcher-btn:hover .team-chevron {
        color: var(--primary);
    }

    .team-switcher-btn:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }

    .team-switcher-btn .team-chevron {
        position: absolute;
        right: 6px;
        top: 50%;
        transform: translateY(-50%);
        font-size: 0.75rem !important;
        color: var(--grey-secondary);
        flex-shrink: 0;
        margin: 0 !important;
    }

    .team-single {
        padding: 4px 0;
    }

    .team-single.team-loading .team-switcher-value {
        color: var(--grey-secondary);
        font-style: italic;
    }

    .team-chevron {
        flex-shrink: 0;
    }

    .team-dropdown {
        position: absolute;
        top: calc(100% + 8px);
        left: 0;
        right: auto;
        min-width: 220px;
        max-width: 280px;
        background: var(--white);
        border: 1px solid var(--border-strong);
        border-radius: var(--radius-card);
        box-shadow: 0 12px 28px rgba(0, 0, 0, .18), 0 4px 10px rgba(0, 0, 0, .1);
        z-index: 100;
        overflow: hidden;
        max-height: 240px;
        overflow-y: auto;
    }

    .team-dropdown::before {
        content: '';
        position: absolute;
        top: -6px;
        left: 18px;
        width: 10px;
        height: 10px;
        background: var(--white);
        border-top: 1px solid var(--border-strong);
        border-left: 1px solid var(--border-strong);
        transform: rotate(45deg);
        pointer-events: none;
    }

    .team-option {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        width: 100%;
        padding: 10px 14px 10px 18px;
        border: none;
        border-left: 3px solid transparent;
        border-bottom: 1px solid var(--border-color);
        background: var(--white);
        cursor: pointer;
        font-size: 0.85rem;
        color: var(--grey);
        transition: background 0.15s, color 0.15s, border-color 0.15s;
        text-align: left;
        min-width: 0;
    }

    .team-option:last-child {
        border-bottom: none;
    }

    .team-option:hover {
        background: var(--status-info-soft);
        color: var(--primary);
    }

    .team-option.active {
        background: var(--status-info-soft);
        color: var(--primary);
        font-weight: 600;
        border-left-color: var(--primary);
    }

    .team-option-name {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .team-role {
        font-size: 0.65rem;
        color: var(--grey-secondary);
        text-transform: capitalize;
        flex-shrink: 0;
        white-space: nowrap;
    }
</style>

